import React, { useState, useEffect } from "react";
import {
  IconSettings,
  IconCheck,
  IconDeviceFloppy,
  IconBuildingBank,
  IconBarcode,
  IconShieldLock,
  IconCash
} from "@tabler/icons-react";
import { useAssetSettings, updateAssetSettings } from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetSettingsTab() {
  const { data: settings, error, isLoading, mutate } = useAssetSettings();

  const [formData, setFormData] = useState({
    asset_id_prefix: "AST-",
    default_depreciation_method: "Straight Line",
    default_capitalization_threshold: "5000.00",
    auto_generate_qr: 1,
    require_verification_approval: 1,
    default_asset_account: "Fixed Assets",
    default_dep_expense_account: "Depreciation Expense",
    default_acc_dep_account: "Accumulated Depreciation",
    default_disposal_gain_loss_account: "Gain/Loss on Asset Disposal"
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        asset_id_prefix: settings.asset_id_prefix || "AST-",
        default_depreciation_method: settings.default_depreciation_method || "Straight Line",
        default_capitalization_threshold: String(settings.default_capitalization_threshold || "5000.00"),
        auto_generate_qr: settings.auto_generate_qr !== undefined ? settings.auto_generate_qr : 1,
        require_verification_approval:
          settings.require_verification_approval !== undefined ? settings.require_verification_approval : 1,
        default_asset_account: settings.default_asset_account || "Fixed Assets",
        default_dep_expense_account: settings.default_dep_expense_account || "Depreciation Expense",
        default_acc_dep_account: settings.default_acc_dep_account || "Accumulated Depreciation",
        default_disposal_gain_loss_account:
          settings.default_disposal_gain_loss_account || "Gain/Loss on Asset Disposal"
      });
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateAssetSettings({
        ...formData,
        default_capitalization_threshold: Number(formData.default_capitalization_threshold || 0)
      });
      toast.success("Asset configuration settings saved!");
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to save settings");
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <IconSettings className="w-6 h-6 text-restro-green" />
          Asset Management Configuration & Policy Settings
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Configure tagging numbering conventions, capitalization thresholds, and Chart of Accounts integration.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Numbering & Barcode Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconBarcode className="w-5 h-5 text-gray-400" />
            Tagging, QR Code & Identification
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Asset Code Prefix *
              </label>
              <input
                type="text"
                value={formData.asset_id_prefix}
                onChange={(e) => setFormData({ ...formData, asset_id_prefix: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green font-mono font-bold text-gray-900 dark:text-white"
                required
              />
              <span className="text-[11px] text-gray-400">Generates codes like AST-0001, AST-0002</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Automatic QR Tag Creation
              </label>
              <select
                value={formData.auto_generate_qr}
                onChange={(e) => setFormData({ ...formData, auto_generate_qr: Number(e.target.value) })}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-medium"
              >
                <option value={1}>Enabled - Generate QR upon Receiving</option>
                <option value={0}>Disabled - Manual generation only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Financial & Capitalization Policy Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconCash className="w-5 h-5 text-gray-400" />
            Capitalization & Depreciation Policy
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Capitalization Threshold (NPR) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.default_capitalization_threshold}
                onChange={(e) => setFormData({ ...formData, default_capitalization_threshold: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green font-semibold text-gray-900 dark:text-white"
                required
              />
              <span className="text-[11px] text-gray-400">
                Purchases below this amount are expensed or tracked as low-value items.
              </span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Default Depreciation Method
              </label>
              <select
                value={formData.default_depreciation_method}
                onChange={(e) => setFormData({ ...formData, default_depreciation_method: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200 font-medium"
              >
                <option value="Straight Line">Straight Line (SLM)</option>
                <option value="Written Down Value">Written Down Value / Reducing Balance (WDV)</option>
                <option value="Units of Production">Units of Production</option>
              </select>
            </div>
          </div>
        </div>

        {/* Chart of Accounts Default Mapping */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconBuildingBank className="w-5 h-5 text-gray-400" />
            General Ledger (GL) Account Mapping
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Default Fixed Asset GL Account
              </label>
              <input
                type="text"
                value={formData.default_asset_account}
                onChange={(e) => setFormData({ ...formData, default_asset_account: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Default Accumulated Depreciation Account
              </label>
              <input
                type="text"
                value={formData.default_acc_dep_account}
                onChange={(e) => setFormData({ ...formData, default_acc_dep_account: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Default Depreciation Expense Account
              </label>
              <input
                type="text"
                value={formData.default_dep_expense_account}
                onChange={(e) => setFormData({ ...formData, default_dep_expense_account: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Default Gain/Loss on Disposal Account
              </label>
              <input
                type="text"
                value={formData.default_disposal_gain_loss_account}
                onChange={(e) => setFormData({ ...formData, default_disposal_gain_loss_account: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
          >
            <IconDeviceFloppy className="w-4 h-4" />
            Save Configuration
          </button>
        </div>
      </form>
    </div>
  );
}
